import { ReactionTypeT, StatusTypeT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { Pencil } from 'lucide-react';

import { usePatientEligibilitiesQuery } from '@/entities/eligibility';
import {
  usePatientAllergiesQuery,
  usePatientFormularyQuery,
  usePreviewPriorAuthQuery,
} from '@/entities/rx';
import {
  getAllergyQualifierLabels,
  toAllergyRowModel,
} from '@/features/rx-form/model/allergiesTableModel';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';

import { useAllergyEditing } from '../../model/useAllergyEditing';
import { useAllergySelection } from '../../model/useAllergySelection';
import { usePatientFormularyParams } from '../../model/usePatientFormularyParams';
import { usePriorAuthBannerModel } from '../../model/usePriorAuthBannerModel';
import type { useRxWizardForm } from '../../model/useRxWizardForm';
import { BenefitsCoordinationPanel } from '../BenefitsCoordinationPanel';
import { PrescriberInfo } from '../PrescriberInfo';
import { AllergySelector } from '../selectors/AllergySelector';
import { RxDraftSummaryNode } from '../RxDraftSummaryNode';

type RxFormApi = ReturnType<typeof useRxWizardForm>['form'];

export interface ReviewStepProps {
  userId: string;
  form: RxFormApi;
  errorMessage?: string | null;
  /** Optional callback to navigate back to details step for editing. */
  onEditDetails?: () => void;
  /** Opens the diagnosis picker without leaving the review step. */
  onEditDiagnosis?: () => void;
  readOnly?: boolean;
  /** Controlled expedited flag for PA initiation. */
  paExpedited?: boolean;
  /** Called when the clinician toggles the expedited checkbox. */
  onPaExpeditedChange?: (checked: boolean) => void;
  /**
   * Optional override pinning the formulary lookup to a specific
   * ``PatientEligibilityId``. Surfaced in the Details step's picker
   * and threaded through here so the Review step queries the same
   * coverage. ``null`` means the server picks one.
   */
  formularyEligibilityOverride?: number | null;
  /** When provided, the eligibility picker is shown in the Review step. */
  onFormularyEligibilityOverrideChange?: (value: number | null) => void;
}

const ELIGIBILITY_AUTO_VALUE = 'auto';

export function ReviewStep({
  userId,
  form,
  errorMessage,
  onEditDetails,
  onEditDiagnosis,
  readOnly = false,
  paExpedited,
  onPaExpeditedChange,
  formularyEligibilityOverride = null,
  onFormularyEligibilityOverrideChange,
}: ReviewStepProps) {
  const values = form.state.values;
  const patientAllergiesQuery = usePatientAllergiesQuery(userId);
  const selection = useAllergySelection(userId);
  const allergies = patientAllergiesQuery.data ?? [];
  const {
    cancelEditing,
    draft,
    editError,
    editMutation,
    editingAllergyId,
    save,
    setDraft,
    startEditing,
  } = useAllergyEditing(userId);

  const medicationDisplayName =
    values.kind === 'coded'
      ? (values.selectedMedication?.DisplayName ?? values.selectedMedication?.DrugName ?? '—')
      : (values.compoundDescription ?? '—');
  const pharmacyDisplayName = values.selectedPharmacy?.StoreName ?? '';

  const eligibilitiesQuery = usePatientEligibilitiesQuery(userId);
  const priorAuthParams = usePriorAuthBannerModel(values, formularyEligibilityOverride);
  const priorAuthPreviewQuery = usePreviewPriorAuthQuery({
    userId,
    params: priorAuthParams,
  });

  const formularyParams = usePatientFormularyParams(form, formularyEligibilityOverride);
  const formularyQuery = usePatientFormularyQuery({ userId, params: formularyParams });

  const previewData = priorAuthPreviewQuery.isPlaceholderData
    ? undefined
    : priorAuthPreviewQuery.data;
  const formularyData = formularyQuery.isPlaceholderData ? undefined : formularyQuery.data;
  const hasCurrentFormularyResponse = formularyData !== undefined;
  const hasCurrentPreviewResponse = previewData !== undefined;

  // Prefer formulary data fetched specifically for the medication selection
  // (richer alternatives + copays); fall back to whatever rode along with
  // the PA-preview when the formulary endpoint hasn't responded yet.
  const formularyForPanel = formularyData?.formulary ?? previewData?.formulary;
  const payerForPanel = formularyData?.payerInformation ?? previewData?.payerInformation;

  return (
    <div className="flex flex-col gap-6">
      <div
        className={
          readOnly
            ? 'rounded-[12px] border border-border/70 bg-surface-sunken p-4 shadow-sm dark:bg-background/55'
            : ''
        }
      >
        <RxDraftSummaryNode
          medicationDisplayName={medicationDisplayName}
          directions={values.directions}
          quantity={values.quantity}
          refills={values.refills}
          daysSupply={values.daysSupply}
          effectiveDate={values.effectiveDate}
          pharmacyDisplayName={pharmacyDisplayName}
          pharmacyNotes={values.pharmacyNotes}
          noSubstitutions={values.noSubstitutions}
          diagnosis={values.selectedDiagnosis}
          onEdit={readOnly ? undefined : onEditDetails}
          onEditDiagnosis={readOnly ? undefined : onEditDiagnosis}
        />

        <div
          className={
            readOnly ? 'mt-6 border-t border-border/60 pt-5' : 'border-y border-border/50 py-3'
          }
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs font-medium text-muted-foreground">Allergies</div>
                {patientAllergiesQuery.isLoading ? (
                  <div className="mt-1 text-[15px] font-medium leading-5">Loading…</div>
                ) : null}

                {!patientAllergiesQuery.isLoading &&
                !patientAllergiesQuery.isError &&
                allergies.length === 0 ? (
                  <div className="mt-1 text-[15px] font-medium leading-5">None recorded</div>
                ) : null}
              </div>

              {!readOnly && !patientAllergiesQuery.isLoading && !patientAllergiesQuery.isError ? (
                <AllergySelector selection={selection} />
              ) : null}
            </div>

            {patientAllergiesQuery.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not load allergies</AlertTitle>
                <AlertDescription>
                  <div>The patient’s allergy record is unavailable.</div>
                  <div className="pt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      radius="pill"
                      onClick={() => void patientAllergiesQuery.refetch()}
                    >
                      Retry
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            ) : null}

            {!patientAllergiesQuery.isLoading &&
            !patientAllergiesQuery.isError &&
            allergies.length > 0 ? (
              <div className="space-y-2 divide-y divide-border/50">
                {allergies.map((allergy, index) => {
                  const row = toAllergyRowModel(allergy, index);
                  const qualifierLabels = getAllergyQualifierLabels(allergy);
                  const hasReaction = row.reaction !== '—';
                  const id =
                    allergy.PatientAllergyId == null ? null : String(allergy.PatientAllergyId);
                  const isEditing = id != null && id === editingAllergyId && draft != null;

                  return (
                    <div key={row.key} className="pt-3 first:pt-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate text-[15px] font-medium leading-5">
                            {row.allergen}
                          </div>
                          {!isEditing && qualifierLabels.length > 0 ? (
                            <div className="mt-0.5 text-sm font-medium text-muted-foreground">
                              {qualifierLabels.join(' · ')}
                            </div>
                          ) : null}
                          {!isEditing && hasReaction ? (
                            <div className="mt-0.5 truncate text-sm text-muted-foreground">
                              {row.reaction}
                            </div>
                          ) : null}
                        </div>
                        {isEditing && !readOnly ? (
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              radius="pill"
                              onClick={() => void save()}
                              disabled={editMutation.isPending}
                            >
                              {editMutation.isPending ? 'Saving…' : 'Save'}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              radius="pill"
                              onClick={cancelEditing}
                              disabled={editMutation.isPending}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : !readOnly ? (
                          <Button
                            type="button"
                            size="icon-sm"
                            variant="ghost"
                            radius="pill"
                            aria-label={`Edit ${row.allergen} allergy`}
                            disabled={allergy.PatientAllergyId == null}
                            onClick={() => startEditing(allergy)}
                          >
                            <Pencil className="size-4" aria-hidden />
                          </Button>
                        ) : null}
                      </div>

                      {editError && isEditing ? (
                        <div className="mt-2 text-xs text-destructive">{editError}</div>
                      ) : null}

                      {isEditing ? (
                        <div className="grid grid-cols-1 gap-2 @md:grid-cols-2">
                          <div className="space-y-1">
                            <div className="text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
                              Reaction
                            </div>
                            <Input
                              value={draft.reaction}
                              onChange={(e) =>
                                setDraft({ ...draft, reaction: e.currentTarget.value })
                              }
                              placeholder="Optional"
                              disabled={editMutation.isPending}
                            />
                          </div>

                          <div className="space-y-1">
                            <div className="text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
                              Type
                            </div>
                            <Select
                              value={draft.reactionType}
                              onValueChange={(value) =>
                                setDraft({
                                  ...draft,
                                  reactionType: value as typeof draft.reactionType,
                                })
                              }
                              disabled={editMutation.isPending}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={ReactionTypeT.Allergy}>Allergy</SelectItem>
                                <SelectItem value={ReactionTypeT.AdverseReaction}>
                                  Adverse reaction
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1">
                            <div className="text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
                              Status
                            </div>
                            <Select
                              value={draft.statusType}
                              onValueChange={(value) =>
                                setDraft({
                                  ...draft,
                                  statusType: value as typeof draft.statusType,
                                })
                              }
                              disabled={editMutation.isPending}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={StatusTypeT.Active}>Active</SelectItem>
                                <SelectItem value={StatusTypeT.Inactive}>Inactive</SelectItem>
                                <SelectItem value={StatusTypeT.Deleted}>Deleted</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1">
                            <div className="text-[10px] tracking-[0.08em] text-muted-foreground uppercase">
                              Onset
                            </div>
                            <Input
                              value={draft.onsetDate}
                              onChange={(e) =>
                                setDraft({ ...draft, onsetDate: e.currentTarget.value })
                              }
                              placeholder="YYYY-MM-DD (optional)"
                              disabled={editMutation.isPending}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <BenefitsCoordinationPanel
        payerInformation={payerForPanel}
        formulary={formularyForPanel}
        benefits={previewData?.benefits}
        medication={values.kind === 'coded' ? values.selectedMedication : null}
        priorAuthOutcome={previewData?.outcome ?? null}
        expedited={paExpedited}
        onExpeditedChange={onPaExpeditedChange}
        hasCoverageResponse={hasCurrentFormularyResponse || hasCurrentPreviewResponse}
        isLoadingBenefits={priorAuthPreviewQuery.isFetching || formularyQuery.isFetching}
      />

      {onFormularyEligibilityOverrideChange ? (
        <ReviewEligibilityPicker
          eligibilities={eligibilitiesQuery.data?.items ?? []}
          isLoading={eligibilitiesQuery.isLoading}
          value={formularyEligibilityOverride}
          onChange={onFormularyEligibilityOverrideChange}
          resolvedEligibilityId={
            previewData?.patientEligibilityId ?? formularyData?.patientEligibilityId ?? null
          }
        />
      ) : null}

      <PrescriberInfo />

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>Submission issue</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

interface ReviewEligibilityPickerProps {
  eligibilities: Array<{
    PatientEligibilityId?: number | null;
    PayerName?: string | null;
    PlanName?: string | null;
  }>;
  isLoading: boolean;
  value: number | null;
  onChange: (value: number | null) => void;
  resolvedEligibilityId: number | null;
}

function ReviewEligibilityPicker({
  eligibilities,
  isLoading,
  value,
  onChange,
  resolvedEligibilityId,
}: ReviewEligibilityPickerProps) {
  const options = eligibilities.filter(
    (
      entry
    ): entry is {
      PatientEligibilityId: number;
      PayerName?: string | null;
      PlanName?: string | null;
    } => typeof entry.PatientEligibilityId === 'number'
  );

  if (isLoading || options.length <= 1) {
    return null;
  }

  const selectValue =
    value !== null && options.some((e) => e.PatientEligibilityId === value)
      ? String(value)
      : ELIGIBILITY_AUTO_VALUE;

  const handleChange = (next: string) => {
    if (next === ELIGIBILITY_AUTO_VALUE) {
      onChange(null);
      return;
    }
    const parsed = Number(next);
    onChange(Number.isFinite(parsed) ? parsed : null);
  };

  const resolvedEligibility = options.find(
    (entry) => entry.PatientEligibilityId === resolvedEligibilityId
  );
  const autoLabel =
    resolvedEligibility?.PlanName ?? resolvedEligibility?.PayerName ?? 'Patient’s primary plan';

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-muted-foreground">Insurance plan</span>
      <Select value={selectValue} onValueChange={handleChange}>
        <SelectTrigger size="sm" className="h-7 px-2 text-[11px]">
          <SelectValue placeholder="Patient’s primary plan" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ELIGIBILITY_AUTO_VALUE}>{autoLabel}</SelectItem>
          {options.map((entry) => {
            const id = entry.PatientEligibilityId;
            const label = entry.PayerName ?? entry.PlanName ?? `Coverage #${id}`;
            const detail = entry.PlanName && entry.PayerName ? entry.PlanName : null;
            return (
              <SelectItem key={id} value={String(id)}>
                <span className="flex items-baseline gap-2">
                  <span>{label}</span>
                  {detail ? (
                    <span className="text-[10px] text-muted-foreground">{detail}</span>
                  ) : null}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
